/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import template from './segment-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {TTL} from './select-field-constants';
import {FolderResource} from '../../components/api/resources/resource/folder.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-segment-edit-page',
  template
})
export class SegmentEditPageComponent extends AdminResourceEditPageBase {
  readonly TTL = TTL;
  readonly ttlUnits = ['Minutes', 'Hours', 'Days'];

  segmentResults: any[] = [];
  allFolders: any[] | null = null;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(FolderResource) private readonly Folder: any,
    @Inject(SearchResource) private readonly searchResource: any,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.Folder.query().$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.allFolders = page.results || [];
        this.resumeFormDirtyTracking();
      });
    });
  }

  protected afterInitEditingModel(): void {
    this.editingModel.dataSource = this.editingModel.dataSource || 'ADNUNTIUS';

    if (!this.editingModel.timeToLive) {
      this.editingModel.ttlUnit = 'Days';
      this.editingModel.ttlSpec = false;
    } else {
      const inDays = moment.duration(this.editingModel.timeToLive).asDays();
      const inHours = moment.duration(this.editingModel.timeToLive).asHours();
      const inMinutes = moment.duration(this.editingModel.timeToLive).asMinutes();
      this.editingModel.ttlSpec = true;

      if (inDays % 1 === 0) {
        this.editingModel.ttlNumber = inDays;
        this.editingModel.ttlUnit = 'Days';
      } else if (inHours % 1 === 0) {
        this.editingModel.ttlNumber = inHours;
        this.editingModel.ttlUnit = 'Hours';
      } else {
        this.editingModel.ttlNumber = inMinutes;
        this.editingModel.ttlUnit = 'Minutes';
      }
    }

    if (this.isNew) {
      this.editingModel.global = true;
    }

    this.editingModel.folders = _.map(this.editingModel.folders, (folder: any) => {
      return _.isObject(folder) ? (folder as any).id : folder;
    });
  }

  protected onBeforeSubmit(): void {
    this.editingModel.timeToLive = this.editingModel.ttlSpec
      ? 'P' + (this.editingModel.ttlUnit !== 'Days' ? 'T' : '') + this.editingModel.ttlNumber + this.editingModel.ttlUnit.charAt(0)
      : null;
  }

  segSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({
      q: searchElement.search,
      types: 'UserSegment',
      objectState: 'ACTIVE',
      dataSource: 'ADNUNTIUS'
    }).then((data: any) => {
      this.ngZone.run(() => {
        this.segmentResults = data.searchResults || [];
      });
    });
  };

  onFolderChecklistChanged(): void {
    this.markExternalFormDirty();
  }
}
